import React, { useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import "dayjs/locale/es";

import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { esES } from "@mui/x-date-pickers/locales";

import axios from "../../api/axios";

import GeneralContext from "../GeneralContext/GeneralContext";
import BreadcrumbStandard from "../GeneralComponents/BreadcrumbStandard";
import HourglassLoader from "../GeneralComponents/HourglassLoader";
import ModalStandard from "../GeneralComponents/ModalStandard";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import PageBody from "../GeneralComponents/PageBody";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageError from "../GeneralComponents/PageError";

import FasHiddenPreview from "./FasHiddenPreview";
import {toSnakeCase} from "../utils/general_utils";

import {
  APROVED,
  NO_EES,
  NO_EES_TEMP,
  QUESTIONS,
  REJECTED,
  REJECT_F,
  REJECT_F_OTRO,
  ROL_ADMIN_SUBTEL,
  ROL_REP_EES,
  ROL_REP_EES_TEMP,
  ROL_VAL_SUBTEL,
} from "../../Constants/Constants";

import { getValueByKey } from "../utils/general_utils";

export default function FasReject() {
  const { pageTitle, setPageTitle, setDrawerHidding, userSesion, includeRol } =
    useContext(GeneralContext);

  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(true);
  const [techTypes, setTechTypes] = useState([]);

  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const [showQuestions, setShowQuestions] = useState(true);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill("no"));

  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [canSign, setCanSign] = useState(false);

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate();
  const [modalError, setModalError] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const handleClick = (value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      rejectOption: prevFormData.rejectOption === value ? null : value,
    }));
  };
  const { id } = useParams();
  const links = [
    { name: "Inicio", path: "/home" },
    { name: "Listado FAS", path: "/fas_lista" },
    { name: "Firma FAS", path: "/fas_firma/" + id },
  ];

  const [formData, setFormData] = useState({
    rut: userSesion.id,
    operator_name: "",
    rbd: "",
    estado: APROVED,
    ees_name: "",
    ees_managers: ["", ""],
    ees_managers_email: ["", ""],
    ees_temporal_manager_list: [""],
    ees_temporal_manager: "",
    ees_temporal_manager_email: "",
    ees_temporal_manager_nombres: "",
    ees_temporal_manager_apellidos: "",
    tech_id: "",
    int_speed_down: "",
    installation_date: dayjs(),
    speed_up: "",
    speed_down: "",
    latency: "",
    packet_loss: "",
    evidence: [],
    answers: [],
    rejectOption: null,
  });

  function getTechType() {
    axios
      .getTechTypes()
      .then((response) => {
        setTechTypes(response.data);
      })
      .catch((error) => {});
  }
  const fetchData = async (query) => {
    setLoading(true);
    axios
      .getFas(id)
      .then((response) => {
        setFormData({
          ...response.data.datos,
          fecha_creacion: response.data.fecha_creacion.replace("T", " "),
          installation_date: dayjs(response.data.datos["installation_date"]),
          ees_managers_email: response.data.datos["ees_managers_email"]
            ? response.data.datos["ees_managers_email"]
            : ["", ""],
          estado: response.data.estado,
        });
        const flag =
          includeRol([ROL_REP_EES, ROL_REP_EES_TEMP]) &&
          (response.data.estado === NO_EES ||
            response.data.estado === NO_EES_TEMP);
        setShowQuestions(flag);
        setAnswers(Array(QUESTIONS.length).fill(flag ? "no" : "yes"));
      })
      .catch((error) => {
        setDocumentVisible(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const rejectDocSubtel = async (event) => {
    event.preventDefault();
    if (!submitConfirm) {
      setSubmitConfirm(!submitConfirm);
      return;
    }
    setLoadingButton(true);
    const form = new FormData();
    const localFormData = {
      ...formData,
      installation_date: formData["installation_date"]
        .locale("es")
        .format("YYYY/MM/DD HH:MM:ss")
        .toString(),
    };

    form.append("data", JSON.stringify(localFormData));
    axios
      .rejectDocSubtel("fas", id, form)
      .then((response) => {
        navigate("/fas_lista");
      })
      .catch((error) => {})
      .finally(() => {
        setLoadingButton(false);
        setSubmitConfirm(false);
      });
  };

  useEffect(() => {
    setIsSubmitEnabled(answers.every((answer) => answer === "yes"));
    setFormData((prevFormData) => ({
      ...prevFormData,
      answers: answers,
    }));
  }, [answers]);

  useEffect(() => {
    setPageTitle("Firma de formulario electrónico de Alta del Servicio (FAS)");
    getTechType();
    fetchData("");
    setDrawerHidding(false);
    setPageLoaded(true);
  }, []);

  useEffect(() => {
    if (
      includeRol([ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL]) &&
      formData["estado"] !== REJECTED &&
      formData["estado"] !== APROVED
    ) {
      setCanSign(true);
    }
  }, [userSesion, formData]);

  if (pageLoaded) {
    return (
      <>
        <PageBody>
          <BreadcrumbStandard links={links} />
          {loading ? (
            <PageBgBlock pad="2rem 9rem">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                  width: "100%",
                  backgroundColor: "white",
                }}
              >
                <CircularProgress />
                <Typography variant="h6" sx={{ marginTop: 2 }}>
                  Cargando...
                </Typography>
              </Box>
            </PageBgBlock>
          ) : loadingButton ? (
            <HourglassLoader />
          ) : documentVisible && canSign ? (
            <>
              <Box>
                <FasHiddenPreview
                  formData={formData}
                  techTypes={techTypes}
                  id={id}
                ></FasHiddenPreview>
              </Box>

              <Stack justifyContent="center" spacing={2}>
                <Box>
                  <List
                    sx={{
                      backgroundColor: "#BBDEFB",
                      padding: "1rem 0",
                      borderRadius: "20px",
                      marginTop: "1rem",
                    }}
                  >
                    <ListItem key="header" sx={{ justifyContent: "center" }}>
                      <Typography variant="h1" align="center">
                        ENCUESTA DE SATISFACCIÓN EES
                      </Typography>
                    </ListItem>
                    <Divider sx={{ border: "1px solid white" }} />
                    {QUESTIONS.map((question, index) => (
                      <React.Fragment key={index}>
                        <ListItem
                          sx={{ paddingLeft: "9rem", paddingRight: "9rem" }}
                        >
                          <Grid
                            container
                            spacing={2}
                            sx={{ dispaly: "flex", width: "100%" }}
                          >
                            <Grid size={9}>
                              <ListItemText
                                primary={
                                  <Typography variant="body1">
                                    {question}
                                  </Typography>
                                }
                              />
                            </Grid>
                            <Grid
                              size={3}
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <RadioGroup test_id={toSnakeCase("question_"+index)}
                                disabled={!showQuestions}
                                row
                                value={answers[index]}
                              >
                                <FormControlLabel test_id={toSnakeCase("yes_"+index)}
                                  disabled={!showQuestions}
                                  value="yes"
                                  control={<Radio />}
                                  label="Sí"
                                />
                                <FormControlLabel test_id={toSnakeCase("no_"+index)}
                                  disabled={!showQuestions}
                                  value="no"
                                  control={<Radio />}
                                  label="No"
                                />
                              </RadioGroup>
                            </Grid>
                          </Grid>
                        </ListItem>
                        {index < QUESTIONS.length - 1 && (
                          <Divider sx={{ border: "1px solid white" }} />
                        )}{" "}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>

                {includeRol([ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL]) ? (
                  <PageBgBlock pad="2rem 9rem">
                    <form onSubmit={rejectDocSubtel} id="fas_reject">
                      <Typography variant="h3" color="inherit" noWrap sx={{}}>
                        CAUSALES DE RECHAZO
                      </Typography>

                      <FormControl sx={{ m: 1, width: "100%", spacing: 2 }}>
                        <RadioGroup test_id={toSnakeCase("rejectOption")}
                          name="opciones rechazo"
                          value={formData.rejectOption || ""}
                        >
                          {REJECT_F.map((reject, index) => (
                            <FormControlLabel test_id={toSnakeCase("rejectOption_"+index)}
                              key={index}
                              value={reject}
                              control={
                                <Radio onClick={() => handleClick(reject)} />
                              }
                              label={reject}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>

                      <Typography variant="h3" color="inherit" noWrap sx={{}}>
                        JUSTIFICACIÓN DE SUBTEL
                      </Typography>

                      <FormControl sx={{ m: 1, width: "100%", spacing: 2 }}>
                        <TextField test_id={toSnakeCase("justificacion_subtel")}
                          multiline
                          minRows={4}
                          maxRows={30}
                          id="outlined-required"
                          placeholder="Texto aquí"
                          size="small"
                          value={formData.justificacion_subtel}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              justificacion_subtel: event.target.value,
                            })
                          }
                        />
                      </FormControl>
                    </form>
                  </PageBgBlock>
                ) : null}
                <>
                  <Stack sx={{ paddingBottom: "3rem" }}>
                    <Grid container spacing={2} sx={{ paddingTop: "2rem" }}>
                      {submitConfirm ? (
                        <Grid
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          size={12}
                          sx={{ paddingBottom: "3rem" }}
                        >
                          <Alert
                            icon={false}
                            variant="filled"
                            severity="warning"
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              "& .MuiAlert-message": {
                                textAlign: "center",
                                width: "inherit",
                              },
                            }}
                          >
                            <Typography
                              variant="body"
                              color="inherit"
                              sx={{ textAlign: "center" }}
                            >
                              Revise que todos los campos contienen la
                              información correcta antes de firmar el formulario
                              FAS. Al hacer clic en 'Firmar y enviar', el
                              documento quedará firmado sin opción de
                              modificación.
                            </Typography>
                          </Alert>
                        </Grid>
                      ) : (
                        ""
                      )}
                      <Grid size={2}></Grid>
                      <Grid size={8}>
                        <PageButtonWhite
                          form="fas_reject"
                          loading={loadingButton || (formData.rejectOption === REJECT_F_OTRO && !formData.justificacion_subtel?.trim())}
                          width="100%"
                        >
                          {" "}
                          Rechazar
                        </PageButtonWhite>
                      </Grid>

                      <Grid size={2}></Grid>
                    </Grid>
                  </Stack>
                </>
              </Stack>
            </>
          ) : (
            <PageError>
              El documento al que intenta acceder no esta disponible para ser
              firmado.
            </PageError>
          )}
          <ModalStandard
            open={open}
            error={modalError}
            title={modalContent}
            btnText="Volver a listado"
            onClick={() => navigate("/fas_lista")}
            onClose={handleClose}
          ></ModalStandard>
        </PageBody>
      </>
    );
  }
}
